/**
 * Generic undo toast — shown for ~6 seconds with a progress bar and an
 * Undo button. After the timer expires the action commits silently.
 *
 * Used by Mark-done (close topic + close source Todoist task) so a misclick
 * is reversible. Undo explicitly reopens both records.
 */
import { useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { Undo2, X } from 'lucide-react'

type Props = {
  message: string
  onUndo: () => void | Promise<void>
  onDismiss?: () => void
  durationMs?: number
}

export default function UndoToast({
  message, onUndo, onDismiss, durationMs = 6000,
}: Props) {
  const [progress, setProgress] = useState(100)
  const [done, setDone] = useState(false)
  const undoneRef = useRef(false)

  // The parent passes a fresh inline `onDismiss` every render and re-renders
  // constantly (the topics query runs with staleTime 0 and is invalidated on
  // every WebSocket/poll event). Hold the latest callback in a ref so the
  // countdown effect can depend on `durationMs` alone — listing `onDismiss`
  // here would restart the timer (resetting `start`) on every re-render and the
  // toast would never auto-commit. Do NOT add onDismiss back to the effect deps.
  const onDismissRef = useRef(onDismiss)
  useEffect(() => { onDismissRef.current = onDismiss }, [onDismiss])

  useEffect(() => {
    const start = performance.now()
    let raf = 0
    function tick(now: number) {
      const elapsed = now - start
      const remain = Math.max(0, 1 - elapsed / durationMs)
      setProgress(remain * 100)
      if (remain > 0 && !undoneRef.current) {
        raf = requestAnimationFrame(tick)
      } else {
        setDone(true)
        onDismissRef.current?.()
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [durationMs])

  if (done) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'fixed left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)]',
        'bottom-[max(env(safe-area-inset-bottom,0px),16px)]',
      )}
    >
      <div className="relative overflow-hidden rounded-xl bg-stone-900 text-white shadow-2xl">
        {/* Progress bar across the top */}
        <div
          className="absolute top-0 left-0 h-0.5 bg-blue-400 transition-all"
          style={{ width: `${progress}%` }}
          aria-hidden
        />
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex-1 text-sm">{message}</span>
          <button
            onClick={async () => {
              undoneRef.current = true
              setDone(true)
              await onUndo()
            }}
            className="inline-flex items-center gap-1 text-sm font-semibold text-blue-300 hover:text-blue-200 px-2 py-1 rounded"
          >
            <Undo2 className="w-3.5 h-3.5" />
            Undo
          </button>
          <button
            onClick={() => { setDone(true); onDismiss?.() }}
            className="text-stone-400 hover:text-white p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
